import React, { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './Sandbox.module.css';
import globalStyles from '../../App.module.css';
import Board from '../Board';
import { GameContext } from '../Providers/GameContext';
import { useTheme } from '../Providers/ThemeContext';
import { useLanguage } from '../Providers/LanguageContext';
import GlobalHeader from '../GlobalHeader';
import AdSenseLayout from '../AdSense/AdSenseLayout';
import WispyStars from '../Tutorials/WispyStars';
import FENHistory from '../../services/zodiacEngine/Fen/FENHistory';
import GameInfo from '../GameInfo/GameInfo';
import TurnPopover from '../TurnPopover/TurnPopover';
import { PieceType, Mode, IPiece, Position } from '../../services/zodiacEngine/types';
import { allPieceTypes } from '../../services/zodiacEngine/const';
import PiecePalette, { PiecePaletteRef } from './PiecePalette';
import ZodiacEngineService from '../ZodiacEngineService';
import { Piece } from '../../services/zodiacEngine/Piece';
import { playUISound } from '../../utils/UISoundEffects';

function Sandbox() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const {
    gameState,
    potentialMoves,
    potentialSideEffects,
    updateGameState
  } = useContext(GameContext);
  const fenHistoryRef = useRef<any>(null);
  const piecePaletteRef = useRef<PiecePaletteRef>(null);
  const [replayLoaded, setReplayLoaded] = useState(false);

  // Initialize sandbox mode when component mounts
  useEffect(() => {
    const engine = ZodiacEngineService.zodiacEngine;

    // Check if we're loading a replay position
    const takeover = (location.state as any)?.replayTakeover;
    if (takeover?.fen) {
      engine.loadFromFEN(takeover.fen);
      setReplayLoaded(true);
      // Clear router state to prevent re-triggering
      window.history.replaceState({}, document.title);
    } else {
      // Clear the board to ensure no pieces remain from previous session
      engine.loadFromFEN('8/8/8/8/8');
    }

    // Set sandbox mode AFTER loadFromFEN, since loadFromFEN resets mode to Dev
    engine.setGameMode(Mode.Sandbox);
  }, []);

  // After replay FEN loads, mark imported pieces as placed in the palette
  useEffect(() => {
    if (!replayLoaded || !piecePaletteRef.current) return;

    const boardPieces: Array<{type: PieceType, isLight: boolean}> = [];
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const pieces = gameState.board[row][col].Pieces;
        pieces.forEach((piece: any) => {
          boardPieces.push({ type: piece.type, isLight: piece.isLight });
        });
      }
    }

    if (boardPieces.length > 0) {
      piecePaletteRef.current.markBoardPiecesAsPlaced(boardPieces);
      setReplayLoaded(false);
    }
  }, [replayLoaded, gameState]);

  // Track previous board pieces to detect removals (captures, virus kills, etc.)
  const [previousPieces, setPreviousPieces] = useState<Map<string, { type: PieceType; isLight: boolean }>>(new Map());

  // Monitor for piece removals in Sandbox mode and return pieces to palette
  useEffect(() => {
    if (gameState.mode !== Mode.Sandbox) return;

    // Collect all current piece IDs and their info from the board
    const currentPieces = new Map<string, { type: PieceType; isLight: boolean }>();
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const pieces = gameState.board[row][col].Pieces;
        pieces.forEach((piece: any) => {
          currentPieces.set(piece.id, { type: piece.type, isLight: piece.isLight });
        });
      }
    }

    // Only check for removals if we have a previous state (not initial load)
    if (previousPieces.size > 0) {
      previousPieces.forEach((pieceInfo, prevId) => {
        if (!currentPieces.has(prevId) && piecePaletteRef.current) {
          // This piece was removed - return it to the palette
          // Skip viruses as they're not meant to be returned
          if (pieceInfo.type !== PieceType.Virus) {
            piecePaletteRef.current.returnCapturedPiece(
              pieceInfo.type,
              pieceInfo.isLight
            );
          }
        }
      });
    }

    // Update previous state for next comparison
    setPreviousPieces(currentPieces);
  }, [gameState]);

  const handleHome = () => {
    navigate('/');
  };

  // Validate if a piece can be placed on a specific square in sandbox mode
  const isValidSandboxPlacement = (piece: any, targetSquare: any, position: any): boolean => {
    // Check if there's already a piece of the same color on the square
    const existingPieces = targetSquare.Pieces || [];
    const hasEnemyPiece = existingPieces.some((p: any) => p.isLight !== piece.isLight);
    const hasFriendlyPiece = existingPieces.some((p: any) => p.isLight === piece.isLight);


    // VIRUS SPECIAL CASE: Virus can be placed on empty squares OR on animal pieces
    if (piece.type === 'Virus') {
      const animalTypes = ['Bear', 'Crane', 'Frog', 'Goat', 'Monkey', 'Octopus', 'Penguin', 'Rhino', 'Snake', 'Wolf'];

      // Check if there's already a virus on this square
      const hasVirus = existingPieces.some((p: any) => p.type === 'Virus');
      if (hasVirus) {
        return false;
      }

      // Empty square is allowed
      if (existingPieces.length === 0) {
        return true;
      }

      // If there are pieces, only allow if there's an animal (virus can infect animals)
      const hasAnimal = existingPieces.some((p: any) => animalTypes.includes(p.type));
      if (hasAnimal) {
        return true;
      }

      // Block placement on non-animal pieces (Tree, Fungi)
      return false;
    }

    // Can't place on a square with a friendly piece (except trees)
    if (hasFriendlyPiece) {
      // Check if the existing friendly piece is a tree - trees can have other pieces on them
      const friendlyTree = existingPieces.find((p: any) => p.type === 'Tree' && p.isLight === piece.isLight);

      if (!friendlyTree) {
        return false; // Can't place on friendly non-tree piece
      }

      // If there's a friendly tree, check if this piece can climb trees
      // Pieces that can climb: Bear, Crane, Frog, Fungi, Goat, Monkey, Octopus, Snake
      // Pieces that CANNOT climb: Penguin, Rhino, Tree, Virus, Wolf
      const canClimb = ['Bear', 'Crane', 'Frog', 'Fungi', 'Goat', 'Monkey', 'Octopus', 'Snake'].includes(piece.type);

      if (!canClimb) {
        return false; // This piece type cannot climb trees
      }

    }

    // Check if there's an enemy piece - allow climbing enemy trees in sandbox mode
    if (hasEnemyPiece) {
      const enemyTree = existingPieces.find((p: any) => p.type === 'Tree' && p.isLight !== piece.isLight);

      if (enemyTree) {
        // Allow climbing into enemy trees in sandbox mode
        const canClimb = ['Bear', 'Crane', 'Frog', 'Fungi', 'Goat', 'Monkey', 'Octopus', 'Snake'].includes(piece.type);

        if (!canClimb) {
          return false;
        }

        // Check if tree already has a piece in it
        if (existingPieces.length > 1) {
          return false;
        }

      } else {
        // Can't place on enemy non-tree pieces
        return false;
      }
    }

    return true; // Placement is valid
  };

  const handlePieceDragStart = (event: React.DragEvent<HTMLImageElement>, piece: IPiece, position: Position) => {
    event.dataTransfer.setData("piece", JSON.stringify({
      type: piece.type,
      isLight: piece.isLight,
      isFromBoard: true,
      fromRow: position.row,
      fromCol: position.col,
      pieceId: piece.id,
    }));
    event.dataTransfer.effectAllowed = "move";

    // Custom drag image using an offscreen DOM element (must be in DOM for setDragImage)
    const element = event.currentTarget;
    const size = Math.round(Math.max(element.offsetWidth, element.offsetHeight) * 0.85);
    const dragEl = document.createElement('div');
    dragEl.style.position = 'fixed';
    dragEl.style.top = '-1000px';
    dragEl.style.left = '-1000px';
    dragEl.style.width = `${size}px`;
    dragEl.style.height = `${size}px`;
    dragEl.style.pointerEvents = 'none';
    dragEl.style.zIndex = '-1';

    const img = document.createElement('img');
    img.src = element.src;
    img.style.width = `${size}px`;
    img.style.height = `${size}px`;
    dragEl.appendChild(img);
    document.body.appendChild(dragEl);

    event.dataTransfer.setDragImage(dragEl, size / 2, size / 2);

    // Hide the original piece on the board during drag
    element.style.opacity = '0';
    element.addEventListener('dragend', () => {
      element.style.opacity = '';
    }, { once: true });

    requestAnimationFrame(() => {
      document.body.removeChild(dragEl);
    });
  };

  const handleBoardPieceDroppedToSandbox = (pieceType: PieceType, isLight: boolean, fromRow: number, fromCol: number, pieceId: string) => {
    const sourceSquare = gameState.board[fromRow][fromCol];
    const pieceIndex = sourceSquare.Pieces.findIndex((p: any) => p.id === pieceId);
    if (pieceIndex === -1) return;
    sourceSquare.Pieces.splice(pieceIndex, 1);

    // Force re-render so the board reflects the removal
    updateGameState({ ...gameState });

    const audio = new Audio('/sand1.mp3');
    audio.volume = 0.3;
    audio.play().catch(() => {});
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, position: any) => {
    event.preventDefault();
    const pieceData = event.dataTransfer.getData("piece");

    if (!pieceData) return;
    
    const parsedData = JSON.parse(pieceData);
    const { type, isLight, sandboxKey, isRepositioning, isFromBoard, fromRow, fromCol, pieceId } = parsedData;

    // Handle board-to-board piece repositioning
    if (isFromBoard) {
      // Don't allow dropping on the same square
      if (fromRow === position.row && fromCol === position.col) return;

      const sourceSquare = gameState.board[fromRow][fromCol];
      const pieceIndex = sourceSquare.Pieces.findIndex((p: any) => p.id === pieceId);
      if (pieceIndex === -1) return;

      const piece = sourceSquare.Pieces[pieceIndex];
      const targetSquare = gameState.board[position.row][position.col];

      if (!isValidSandboxPlacement(piece, targetSquare, position)) return;

      // Remove from source
      const fromAlgebraic = String.fromCharCode(65 + fromCol) + (fromRow + 1);
      sourceSquare.Pieces.splice(pieceIndex, 1);
      // Place at destination via engine (this adds a ~{placement:...} annotation)
      ZodiacEngineService.zodiacEngine.placePiece(piece, position);

      // Replace the placement annotation with a moved annotation in the last FEN entry
      const toAlgebraic = String.fromCharCode(65 + position.col) + (position.row + 1);
      const colorStr = piece.isLight ? 'Light' : 'Dark';
      const lastIdx = gameState.fenHistory.length - 1;
      if (lastIdx >= 0) {
        gameState.fenHistory[lastIdx] = gameState.fenHistory[lastIdx]
          .replace(/~\{placement:[^}]+\}/, `~{moved:${piece.type},${colorStr},${fromAlgebraic},${toAlgebraic}}`);
      }

      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(console.error);
      return;
    }

    // If this is a repositioning drag, check if it's dropped on the sandbox area
    if (isRepositioning) {
      // Find the sandbox container
      const sandboxContainer = document.querySelector('[class*="paletteForeground"]');
      if (sandboxContainer) {
        const rect = sandboxContainer.getBoundingClientRect();
        const isOverSandbox = event.clientX >= rect.left && 
                             event.clientX <= rect.right && 
                             event.clientY >= rect.top && 
                             event.clientY <= rect.bottom;
        
        if (isOverSandbox) {
          return; // Let PiecePalette handle the repositioning
        } else {
          // Continue with board placement logic below
        }
      }
    }

    // Check if a piece of this type and color is already on the board (Sandbox mode only)
    if (gameState.mode === Mode.Sandbox && piecePaletteRef.current) {
      if (piecePaletteRef.current.isPieceOnBoard(type as PieceType, isLight)) {
        
        // Snap piece back to sandbox
        if (sandboxKey && piecePaletteRef.current) {
          piecePaletteRef.current.handlePieceDropFailed(sandboxKey);
        }
        return;
      }
    }

    // Place the piece on the board in sandbox mode
    let placementSuccessful = false;
    
    try {
      // Ensure we're in sandbox mode before placing piece
      if (gameState.mode !== Mode.Sandbox) {
        ZodiacEngineService.zodiacEngine.setGameMode(Mode.Sandbox);
      }
      
      // Create the piece
      const piece = new Piece(type as PieceType, isLight);
      
      // Set penguin direction for sandbox mode
      if (type === 'Penguin') {
        piece.direction = isLight ? 'down' : 'up';
      }
      
      // Check if placement is valid before actually placing
      const targetSquare = gameState.board[position.row][position.col];
      const canPlace = isValidSandboxPlacement(piece, targetSquare, position);
      
      if (!canPlace) {
        throw new Error(`Cannot place ${piece.type} at this location - invalid placement`);
      }
      
      ZodiacEngineService.zodiacEngine.placePiece(piece, position);

      // VIRUS INFECTION: If placing a virus on an animal, mark the animal as infected
      if (type === 'Virus') {
        const square = gameState.board[position.row][position.col];
        const animalPiece = square.Pieces.find((p: any) => p.type !== 'Virus');
        if (animalPiece) {
          animalPiece.isInfected = true;
        }
      }

      placementSuccessful = true;

      // Play click sound when piece is successfully placed
      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3; // Adjust volume as needed
      audio.play().catch(console.error); // Handle any audio play errors silently
      
    } catch (error) {
      placementSuccessful = false;
    }
    
    // Handle sandbox piece removal based on placement success
    if (sandboxKey && piecePaletteRef.current) {
      if (placementSuccessful) {
        piecePaletteRef.current.handlePiecePlaced(sandboxKey);
      } else {
        piecePaletteRef.current.handlePieceDropFailed(sandboxKey);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  // Handle mobile tap-to-place
  const handleSquareClick = (position: any) => {
    if (!piecePaletteRef.current) return;

    const selectedPiece = piecePaletteRef.current.getSelectedPiece();
    if (!selectedPiece) {
      return;
    }


    // Check if a piece of this type and color is already on the board
    if (piecePaletteRef.current.isPieceOnBoard(selectedPiece.type, selectedPiece.isLight)) {
      return;
    }

    try {
      // Ensure we're in sandbox mode
      if (gameState.mode !== Mode.Sandbox) {
        ZodiacEngineService.zodiacEngine.setGameMode(Mode.Sandbox);
      }

      // Create the piece
      const piece = new Piece(selectedPiece.type, selectedPiece.isLight);

      // Set penguin direction for sandbox mode
      if (selectedPiece.type === 'Penguin') {
        piece.direction = selectedPiece.isLight ? 'down' : 'up';
      }

      // Check if placement is valid
      const targetSquare = gameState.board[position.row][position.col];
      const canPlace = isValidSandboxPlacement(piece, targetSquare, position);

      if (!canPlace) {
        return;
      }

      ZodiacEngineService.zodiacEngine.placePiece(piece, position);

      // VIRUS INFECTION: If placing a virus on an animal, mark the animal as infected
      if (selectedPiece.type === 'Virus') {
        const square = gameState.board[position.row][position.col];
        const animalPiece = square.Pieces.find((p: any) => p.type !== 'Virus');
        if (animalPiece) {
          animalPiece.isInfected = true;
        }
      }

      // Play click sound
      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(console.error);

      // Mark piece as placed and clear selection
      const suffix = selectedPiece.isLight ? 'light' : 'dark';
      const pieceKey = `${selectedPiece.type}-${suffix}-0`; // Mobile pieces don't have unique keys
      piecePaletteRef.current.handlePiecePlaced(pieceKey);
      piecePaletteRef.current.clearSelectedPiece();

    } catch (error) {
    }
  };

  const handleClearBoard = () => {
    // Get all pieces from the board
    const board = gameState.board;
    const piecesToReturn: Array<{type: PieceType, isLight: boolean}> = [];

    // Collect all pieces from all squares and clear the board
    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const pieces = board[row][col].Pieces;
        pieces.forEach((piece: any) => {
          piecesToReturn.push({
            type: piece.type,
            isLight: piece.isLight
          });
        });
        // Clear all pieces from this square
        board[row][col].Pieces = [];
      }
    }

    // Reset FEN to empty board (8/8/8/8/8 for 5 rows x 8 columns)
    ZodiacEngineService.zodiacEngine.loadFromFEN('8/8/8/8/8');

    // Return all pieces to sandbox at random locations
    if (piecePaletteRef.current) {
      piecesToReturn.forEach((pieceInfo) => {
        piecePaletteRef.current?.returnCapturedPiece(pieceInfo.type, pieceInfo.isLight);
      });
    }

    // Play sand sound
    const audio = new Audio('/sand1.mp3');
    audio.volume = 0.4;
    audio.play().catch(console.error);
  };

  return (
    <AdSenseLayout>
      <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''} ${globalStyles.app}`}>
        <WispyStars />
        <GlobalHeader />

        {/* Home button - top left */}
        <button
          className={styles.homeBackButton}
          onClick={() => { playUISound(); handleHome(); }}
          aria-label={t('common.home')}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        {/* Mobile-only back button */}
      <button
        className={styles.mobileBackButton}
        onClick={handleHome}
        aria-label="Back to home"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>

      <TurnPopover
        isThisPlayersTurn={gameState.isLightTurn === true}
        state={gameState.state}
        mode={gameState.mode}
      />

      {/* Left Panel */}
      <div className={styles.leftPanel} style={{ pointerEvents: 'auto', zIndex: 100 }}>
        <div className={styles.leftPanelContent}>
          <h1 className={styles.title}>{t('sandbox.title')}</h1>

          <div className={styles.description}>
            <p>{t('sandbox.description')}</p>
          </div>

          <button
            className={styles.clearBoardButton}
            onClick={handleClearBoard}
            title={t('sandbox.clearBoard')}
          >
            {t('sandbox.clearBoard')}
          </button>

          <button
            className={styles.clearBoardButton}
            onClick={() => piecePaletteRef.current?.tidyPieces()}
            title="Tidy Sandbox"
          >
            Tidy Sandbox
          </button>
        </div>
      </div>

      {/* Game Area */}
      <div className={styles.gameArea}>
        <div className={styles.gameAreaContent}>
          {/* Sand image to the left of the board with piece palette overlay */}
          <div className={styles.sandImageContainer}>
            <img
              src="/images/sand_no_border.png"
              alt="Sand texture"
              className={styles.sandImage}
            />
            <PiecePalette ref={piecePaletteRef} onBoardPieceDroppedToSandbox={handleBoardPieceDroppedToSandbox} />
          </div>

          <div className={styles.boardContainer}>
            <Board
              gameState={gameState}
              uiState={{
                potentialMoves: potentialMoves,
                potentialSideEffects: potentialSideEffects,
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onSquareClick={handleSquareClick}
              onPieceDragStart={handlePieceDragStart}
            />
          </div>

          {/* Right side panels with move history and game info */}
          <div className={styles.rightPanel}>
            <FENHistory ref={fenHistoryRef} />
            <GameInfo />
          </div>
        </div>
      </div>
      </div>
    </AdSenseLayout>
  );
}

export default Sandbox;
